import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";
import JeaButton from "@/components/jea/JeaButton";

const CancelSubscriptionModal = ({ open, subscription, onCancel, onConfirm, submitting }) => {
  const cancelsAt = subscription?.renews_at
    ? new Date(subscription.renews_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "the end of your billing period";
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
            onClick={submitting ? undefined : onCancel}
          />
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-[96] flex items-center justify-center px-4 py-6"
            data-testid="cancel-subscription-modal"
          >
            <div
              className="w-full max-w-[520px] bg-bg-secondary border border-danger/40 rounded-[16px] p-8 shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-danger/15 border border-danger/40 flex items-center justify-center shrink-0">
                  <AlertTriangle size={20} className="text-danger" />
                </div>
                <div className="flex-1">
                  <h2 className="text-white font-bold text-[18px]">Cancel Subscription?</h2>
                  <p className="text-text-secondary text-[13px] mt-2 leading-6">
                    You'll retain full access until <strong className="text-white">{cancelsAt}</strong>.
                    After that, your account moves to <strong className="text-white">read-only mode</strong> —
                    your data stays intact, but new queries and scheduled runs will halt.
                  </p>
                  <ul className="mt-3 text-[12px] text-text-secondary space-y-1 list-disc list-inside leading-5">
                    <li>Existing reports remain viewable</li>
                    <li>Per-query broker charges pause immediately</li>
                    <li>Restart anytime by re-selecting a plan</li>
                  </ul>
                </div>
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={submitting}
                  className="p-2 text-text-secondary hover:text-white"
                  data-testid="cancel-subscription-close"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={submitting}
                  className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[14px] font-semibold transition-colors disabled:opacity-40"
                  data-testid="cancel-subscription-keep"
                >
                  Keep Subscription
                </button>
                <JeaButton
                  variant="danger"
                  onClick={onConfirm}
                  loading={submitting}
                  data-testid="cancel-subscription-confirm"
                >
                  {submitting ? "Cancelling…" : "Yes, Cancel Subscription"}
                </JeaButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CancelSubscriptionModal;
